/**
 * ContentPage
 *
 * Admin page for managing platform taxonomies: property types, amenities, and locations.
 */

import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useTaxonomies, useAmenityCategories } from './hooks';
import type { TaxonomyItem, Location, CancellationPolicy } from './hooks';
import { TaxonomyModal, LocationTree } from './components';
import { QuickRepliesPanel } from './components/QuickRepliesPanel';

type TabType = 'propertyTypes' | 'amenities' | 'locations' | 'policies' | 'quickReplies';

export const ContentPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('propertyTypes');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<TaxonomyItem | null>(null);

  const { items: propertyTypes, isLoading: loadingPT, addItem: addPT, updateItem: updatePT, deleteItem: deletePT } = useTaxonomies('propertyTypes');
  const { items: amenities, isLoading: loadingAm, addItem: addAm, updateItem: updateAm, deleteItem: deleteAm } = useTaxonomies('amenities');
  const { items: locations, isLoading: loadingLoc } = useTaxonomies('locations');
  const { items: policies, isLoading: loadingPol } = useTaxonomies('policies');
  const amenityCategories = useAmenityCategories();

  const groupedAmenities = useMemo(() => {
    if (!amenities) return {};
    return (amenities as TaxonomyItem[]).reduce((acc, amenity) => {
      const category = amenity.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(amenity);
      return acc;
    }, {} as Record<string, TaxonomyItem[]>);
  }, [amenities]);

  const handleAddItem = async (data: Partial<TaxonomyItem>) => {
    if (activeTab === 'propertyTypes') {
      await addPT(data);
    } else if (activeTab === 'amenities') {
      await addAm(data);
    }
  };

  const handleUpdateItem = async (data: Partial<TaxonomyItem>) => {
    if (!editingItem) return;
    if (activeTab === 'propertyTypes') {
      await updatePT(editingItem.id, data);
    } else if (activeTab === 'amenities') {
      await updateAm(editingItem.id, data);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    try {
      if (activeTab === 'propertyTypes') {
        await deletePT(id);
      } else if (activeTab === 'amenities') {
        await deleteAm(id);
      }
    } catch (error: any) {
      toast.error(error.message || 'Cannot delete item');
    }
  };

  const tabs = [
    { id: 'propertyTypes' as const, label: 'Property Types', count: (propertyTypes as TaxonomyItem[]).length },
    { id: 'amenities' as const, label: 'Amenities', count: (amenities as TaxonomyItem[]).length },
    { id: 'locations' as const, label: 'Locations', count: (locations as Location[]).length },
    { id: 'policies' as const, label: 'Cancellation Policies', count: (policies as CancellationPolicy[]).length },
    { id: 'quickReplies' as const, label: 'Quick Replies' },
  ];

  const isLoading = activeTab === 'propertyTypes'
    ? loadingPT
    : activeTab === 'amenities'
    ? loadingAm
    : activeTab === 'locations'
    ? loadingLoc
    : activeTab === 'policies'
    ? loadingPol
    : false; // quickReplies manages its own loading state

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
            <p className="text-gray-500 mt-1">Manage property types, amenities, and locations</p>
          </div>
          {(activeTab === 'propertyTypes' || activeTab === 'amenities') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add {activeTab === 'propertyTypes' ? 'Property Type' : 'Amenity'}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {typeof tab.count === 'number' && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Property Types Tab */}
            {activeTab === 'propertyTypes' && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Icon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usage
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(propertyTypes as TaxonomyItem[]).map((type) => (
                      <tr key={type.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-2xl">{type.icon || '-'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{type.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {type.slug}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">
                            {type.usageCount} listings
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => setEditingItem(type)}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteItem(type.id)}
                            disabled={type.usageCount > 0}
                            className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Amenities Tab */}
            {activeTab === 'amenities' && (
              <div className="space-y-6">
                {Object.entries(groupedAmenities).map(([category, items]) => (
                  <section key={category}>
                    <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-center gap-2">
                      {category}
                      <span className="text-sm font-normal text-gray-500">
                        ({items.length} items)
                      </span>
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-200">
                      {items.map((amenity) => (
                        <div
                          key={amenity.id}
                          className="flex items-center justify-between p-4 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl w-8">{amenity.icon || '-'}</span>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">
                                  {amenity.name}
                                </span>
                                {amenity.isPopular && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                    Popular
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                Used in {amenity.usageCount} listings
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingItem(amenity)}
                              className="px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(amenity.id)}
                              disabled={amenity.usageCount > 0}
                              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {/* Locations Tab */}
            {activeTab === 'locations' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <LocationTree locations={locations as Location[]} />
              </div>
            )}

            {/* Cancellation Policies Tab */}
            {activeTab === 'policies' && (
              <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-200">
                {(policies as CancellationPolicy[]).map((policy) => (
                  <div key={policy.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{policy.name}</h3>
                          {policy.isDefault && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 mt-1">{policy.description}</p>
                      </div>
                      <button className="px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded">
                        Edit
                      </button>
                    </div>
                    <div className="mt-4 flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-500">Refund:</span>{' '}
                        <span className="font-medium text-gray-900">
                          {policy.refundPercentage}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Deadline:</span>{' '}
                        <span className="font-medium text-gray-900">{policy.deadline}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Replies Tab */}
            {activeTab === 'quickReplies' && <QuickRepliesPanel />}
          </>
        )}

        {/* Add/Edit Modal */}
        <TaxonomyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSave={handleAddItem}
          type={activeTab === 'propertyTypes' ? 'propertyType' : 'amenity'}
          categories={amenityCategories}
        />

        {editingItem && (
          <TaxonomyModal
            isOpen={true}
            onClose={() => setEditingItem(null)}
            onSave={handleUpdateItem}
            initialData={editingItem}
            type={activeTab === 'propertyTypes' ? 'propertyType' : 'amenity'}
            categories={amenityCategories}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default ContentPage;
